// src/shared/utils/locationService.ts

/**
 * Location Service
 *
 * Handles browser geolocation with permission requests and
 * Google Geocoding API fallback using Intl timezone
 */

import { loadGoogleMapsApi } from './googleMaps';

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export type LocationSource = 'browser' | 'profile' | 'timezone';

/**
 * Gets coordinates from the browser's timezone using Google Geocoding API.
 * Uses Intl.DateTimeFormat to detect timezone, then geocodes the timezone's
 * representative city to get real coordinates.
 */
export const getCoordinatesFromTimezone = async (): Promise<Coordinates | null> => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Extract city name from IANA timezone (e.g., "Asia/Karachi" -> "Karachi")
    const parts = timezone.split('/');
    const city = parts[parts.length - 1].replace(/_/g, ' ');

    if (!city) {
      return null;
    }

    const google = await loadGoogleMapsApi();
    const geocoder = new google.maps.Geocoder();

    return new Promise((resolve) => {
      geocoder.geocode({ address: city }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
          const location = results[0].geometry?.location;
          if (location) {
            const coordinates: Coordinates = {
              latitude: location.lat(),
              longitude: location.lng(),
            };
            resolve(coordinates);
          } else {
            resolve(null);
          }
        } else {
          resolve(null);
        }
      });
    });
  } catch {
    return null;
  }
};

/**
 * Requests browser geolocation permission and returns coordinates.
 * Falls back to timezone-based geocoding if permission denied or unavailable.
 *
 * @param options - Geolocation options
 * @returns Promise resolving to coordinates
 */
export const requestLocationPermission = async (
  options?: PositionOptions
): Promise<Coordinates> => {
  // Check if geolocation is supported
  if (!navigator.geolocation) {
    const fallback = await getCoordinatesFromTimezone();
    if (fallback) return fallback;
    throw new Error('Could not determine location');
  }

  const defaultOptions: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 300000, // Cache for 5 minutes
    ...options,
  };

  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, defaultOptions);
    });

    const coordinates: Coordinates = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };

    return coordinates;
  } catch (error) {
    const geolocationError = error as GeolocationPositionError;

    // Fallback to timezone-based geocoding
    const fallback = await getCoordinatesFromTimezone();
    if (fallback) return fallback;
    throw new Error('Could not determine location');
  }
};

/**
 * Stores coordinates in localStorage
 */
export const saveCoordinates = (coordinates: Coordinates): void => {
  try {
    localStorage.setItem('user_coordinates', JSON.stringify(coordinates));
  } catch {
  }
};

/**
 * Retrieves coordinates from localStorage
 */
export const getSavedCoordinates = (): Coordinates | null => {
  try {
    const saved = localStorage.getItem('user_coordinates');
    if (saved) {
      const coordinates = JSON.parse(saved) as Coordinates;
      return coordinates;
    }
  } catch {
  }
  return null;
};

/**
 * Gets user location with multiple fallback strategies:
 * 1. Try saved coordinates from localStorage
 * 2. Request browser permission
 * 3. Fallback to timezone-based geocoding via Google API
 *
 * @param forceRefresh - If true, skips localStorage and requests fresh coordinates
 */
export const getUserCoordinates = async (forceRefresh = false): Promise<Coordinates> => {
  // Strategy 1: Use saved coordinates if available and not forcing refresh
  if (!forceRefresh) {
    const saved = getSavedCoordinates();
    if (saved) {
      return saved;
    }
  }

  // Strategy 2: Request browser permission (falls back to timezone geocoding internally)
  const coordinates = await requestLocationPermission();

  // Save for future use
  saveCoordinates(coordinates);

  return coordinates;
};

/**
 * Gets coordinates for search operations with 3-tier priority:
 * 1. Browser geolocation (prompts user to enable location)
 * 2. User's profile address coordinates (from get-profile API)
 * 3. Timezone-based geocoding via Google API
 *
 * @param user - User object with optional address containing lat/lng
 */
export const getSearchCoordinates = async (
  user?: { address?: { latitude?: string; longitude?: string } } | null
): Promise<Coordinates & { source: LocationSource }> => {
  // Tier 1: Browser geolocation (prompts user to enable location)
  try {
    if (navigator.geolocation) {
      // Check permission state first to avoid unnecessary delays
      let permissionGranted = true;
      try {
        if ('permissions' in navigator) {
          const permResult = await navigator.permissions.query({ name: 'geolocation' });
          permissionGranted = permResult.state !== 'denied';
        }
      } catch {
        // permissions API not supported, proceed anyway
      }

      if (permissionGranted) {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 60000, // Accept position cached within last 1 minute
          });
        });
        const coordinates = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          source: 'browser' as LocationSource,
        };
        return coordinates;
      }
    }
  } catch {
  }

  // Tier 2: Profile address coordinates
  if (user?.address?.latitude && user?.address?.longitude) {
    const lat = parseFloat(user.address.latitude);
    const lng = parseFloat(user.address.longitude);
    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
      return { latitude: lat, longitude: lng, source: 'profile' };
    }
  }

  // Tier 3: Timezone-based geocoding via Google API
  const fallback = await getCoordinatesFromTimezone();
  if (fallback) {
    return { ...fallback, source: 'timezone' };
  }

  throw new Error('Could not determine location');
};

/**
 * Checks if geolocation permission is already granted
 */
export const checkLocationPermission = async (): Promise<PermissionState | null> => {
  try {
    if ('permissions' in navigator) {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state;
    }
  } catch {
  }
  return null;
};
